import * as React from "react";

import {
  CartItemInterface,
  LocalizationConsumerInterface,
  ProductInterface,
  TierPrice,
  useCustomerCartId,
  useCustomerSession,
  useLocalizationProvider,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  getCartItemStockStatus,
  isCartItemUnavailable,
  MyBagPresentationItem,
  MyBagPresentationItem_AsAGiftcard,
  MyBagPresentationItem_StockStatus,
  MyBagPresentationOrder,
  MyBagPresentationOrder_PromotionalDiscount,
  MyBagPresentationShippingDetailsOnCart,
  MyBagTotals_Response,
} from "../../model";

import { useMyBagDataSourceIntersection } from "./my-bag-data-source-intersection-provider";
import {
  MyBagItemsRunningState_WorkingModificationsToQuantity,
  MyBagItemsRunningState_WorkingModificationsToQuantity_Status,
  useMyBagItemsRunningState,
} from "./my-bag-items-running-state";

import { useMyBagTotalsDataSourceIntersection } from "./my-bag-totals-data-source-intersection-provider";

interface MyBagPresentationModelConsumerInterface {
  presentationMyBagItems: MyBagPresentationItem[];
  presentationMyBagOrder?: MyBagPresentationOrder;
  presentationMyBagShippingDetailsOnCart?: MyBagPresentationShippingDetailsOnCart;
}
const defaultValue = {} as unknown as MyBagPresentationModelConsumerInterface;
const MyBagPresentationModelContext = React.createContext(defaultValue);

export const MyBagPresentationModelProvider: React.FunctionComponent = (
  props
) => {
  const { isCustomerLoggedIn } = useCustomerSession();
  const { cartId } = useCustomerCartId();
  const localization = useLocalizationProvider();

  const { latest: myBagData } = useMyBagDataSourceIntersection();
  const { latest: myBagTotalsData } = useMyBagTotalsDataSourceIntersection();

  const { workingModificationsToQuantity } = useMyBagItemsRunningState();

  const presentationMyBagItems: MyBagPresentationModelConsumerInterface["presentationMyBagItems"] =
    React.useMemo(() => {
      const presentationMyBagItems: MyBagPresentationItem[] = (
        myBagData?.items || []
      )
        .flatMap((myBagItem) =>
          transformMyBagItemToPresentationProduct(
            isCustomerLoggedIn,
            myBagItem,
            workingModificationsToQuantity,
            cartId
          )
        )
        .filter(Boolean) as MyBagPresentationItem[];

      return presentationMyBagItems;
    }, [isCustomerLoggedIn, myBagData, workingModificationsToQuantity, cartId]);

  const presentationMyBagOrder: MyBagPresentationModelConsumerInterface["presentationMyBagOrder"] =
    React.useMemo(() => {
      if (!myBagData || !myBagData.items || !myBagTotalsData) {
        return undefined;
      }

      const orderTotal_withoutDiscountsOrAnything =
        calculateOrderTotal_withoutAnyDiscountsOrAnything(
          isCustomerLoggedIn,
          myBagData.items,
          presentationMyBagItems,
          cartId
        );

      const saleDiscountsAppliedOnTheOrderTotal =
        calculate_saleDiscountsAppliedOnTheOrderTotal(
          isCustomerLoggedIn,
          myBagData.items,
          presentationMyBagItems,
          cartId
        );

      const [memberDiscountPotential, memberDiscountsAppliedOnTheOrderTotal] =
        calculate_memberDiscountsAppliedOnTheOrderTotal(
          isCustomerLoggedIn,
          myBagData.items,
          presentationMyBagItems
        );

      const allThePromotionalDiscounts =
        figureOut_allThePromotionalDiscounts(myBagData);

      const shippingValueBreakdown =
        figureOut_shippingValueBreakdown(myBagTotalsData);

      const giftCardValueBreakdown =
        figureOut_giftCardValueBreakdown(myBagData);

      const totalValueBreakdown = figureOut_totalValueBreakdown(
        localization,
        myBagData,
        myBagTotalsData
      );

      const areThereAnyGiftcardsInTheOrder =
        figureOut_areThereAnyGiftcardsInTheOrder(myBagData);

      const isThisAVirtualOrder = figureOut_isThisAVirtualOrder(
        areThereAnyGiftcardsInTheOrder,
        myBagData
      );

      const promoCodeBreakdown = figureOut_promoCodeBreakdown(myBagData);

      const myBagOrder: MyBagPresentationModelConsumerInterface["presentationMyBagOrder"] =
        {
          orderValueByItemsBreakdown: {
            orderTotal_withoutDiscountsOrAnything,
            saleDiscountsAppliedOnTheOrderTotal,
            memberDiscountsAppliedOnTheOrderTotal,
            memberDiscountPotential,
            allThePromotionalDiscounts,
          },
          shippingValueBreakdown,
          giftCardValueBreakdown,
          totalValueBreakdown,
          areThereAnyGiftcardsInTheOrder,
          isThisAVirtualOrder,
          promoCodeBreakdown,
        };

      return myBagOrder;
    }, [
      localization,
      isCustomerLoggedIn,
      myBagData,
      presentationMyBagItems,
      cartId,
      myBagTotalsData,
    ]);

  const presentationMyBagShippingDetailsOnCart: MyBagPresentationModelConsumerInterface["presentationMyBagShippingDetailsOnCart"] =
    React.useMemo(() => {
      const presentationMyBagShippingDetailsOnCart =
        generateMyBagPresentationShippingDetailsOnCart(myBagData);
      return presentationMyBagShippingDetailsOnCart;
    }, [myBagData]);

  const value: MyBagPresentationModelConsumerInterface = React.useMemo(
    () => ({
      presentationMyBagItems,
      presentationMyBagOrder,
      presentationMyBagShippingDetailsOnCart,
    }),
    [
      presentationMyBagItems,
      presentationMyBagOrder,
      presentationMyBagShippingDetailsOnCart,
    ]
  );
  return (
    <MyBagPresentationModelContext.Provider value={value}>
      {props.children || []}
    </MyBagPresentationModelContext.Provider>
  );
};

export const useMyBagPresentationModel = () => {
  const context = React.useContext(MyBagPresentationModelContext);
  if (context === defaultValue) {
    throw new Error(
      "useMyBagPresentationModel must be used within a MyBagPresentationModelProvider"
    );
  }
  return context;
};

const getUnavailableItemMessage = (): string => {
  const message = "This product is not available in the requested quantity.";
  const magentoWindow = window as Window & {
    jQuery?: { mage?: { __?: (message: string) => string } };
  };
  if (magentoWindow.jQuery?.mage?.__) {
    return (
      magentoWindow.jQuery.mage.__(
        "This product is not available in the requested quantity."
      ) ?? message
    );
  }
  return message;
};

export const transformMyBagItemToPresentationProduct = (
  isCustomerLoggedIn: boolean,
  item: CartItemInterface,
  workingModificationsToQuantity: MyBagItemsRunningState_WorkingModificationsToQuantity[],
  cartId?: string
) => {
  const thisProduct = item.product;
  const id = item.id;
  const [currentStockStatus, itemQuantityToDisplay] =
    determineStockStatusAndQuantityForAnItem_givenWorkingModifications(
      item,
      workingModificationsToQuantity,
      cartId
    );
  const itemQuantityForPriceCalculation = item.quantity;
  const errors = item.errors ?? [];
  const isUnavailable = isCartItemUnavailable(item, currentStockStatus);
  const serverErrors = errors.map(({ message }) =>
    message.trim() ? message : getUnavailableItemMessage()
  );
  if (item.errors != null && serverErrors.length === 0) {
    serverErrors.push(getUnavailableItemMessage());
  }

  let title;
  if (thisProduct) {
    title = thisProduct.cart_description;
  }
  if (!title) {
    return null;
  }

  let sku;
  if (thisProduct) {
    sku = thisProduct.sku;
  }
  if (!sku) {
    return null;
  }

  let url;
  if (item) {
    const bbnt_product_url = item.bbnt_product_url;
    if (bbnt_product_url) {
      url = bbnt_product_url;
    } else if (thisProduct) {
      if (thisProduct.url_key && thisProduct.url_prefix) {
        url = `/${thisProduct.url_prefix}/${thisProduct.url_key}`;
      } else if (thisProduct.url_rewrites.length > 0) {
        url = `/${thisProduct.url_rewrites[0].url}`;
      } else if (thisProduct.url_key) {
        url = `/${thisProduct.url_key}`;
      }
    }
  }
  if (!url) {
    return null;
  }

  let distinguishingFeature;
  if (thisProduct) {
    distinguishingFeature = thisProduct.item_list_desc;
  }

  const thisItemPrices = item.prices;
  let itemPrice = 0;
  let itemPriceNormally;
  let isMemberPrice = false;
  let isSalePrice = false;
  if (thisItemPrices) {
    const row_total_including_tax__value =
      thisItemPrices.row_total_including_tax.value;
    const defaultItemPrice =
      row_total_including_tax__value / itemQuantityForPriceCalculation;

    const memberDiscountedPriceDescription = item.product.price_tiers
      ? item.product.price_tiers[0]
      : null;
    const memberDiscountedPrice =
      memberDiscountedPriceDescription?.final_price?.value || null;
    const memberDiscountedAmountOff =
      memberDiscountedPriceDescription?.discount?.amount_off || 0;
    const isMemberDiscountPriceValidOnItsOwn =
      memberDiscountedPrice !== null && memberDiscountedPrice !== 0;
    const memberItemPrice_priceNormally =
      (memberDiscountedPrice || 0) + memberDiscountedAmountOff;

    const saleDiscountedPriceDescription = item.product.price_range
      ? item.product.price_range.minimum_price
      : null;
    const isSalesDiscountPriceValidToSet =
      saleDiscountedPriceDescription &&
      saleDiscountedPriceDescription.regular_price.value > 0 &&
      saleDiscountedPriceDescription.regular_price.value >
        saleDiscountedPriceDescription.final_price.value;
    const saleDiscountedPrice = isSalesDiscountPriceValidToSet
      ? saleDiscountedPriceDescription.final_price.value
      : 0;
    const saleItemPrice_priceNormally =
      saleDiscountedPriceDescription?.regular_price.value;

    const doesMemberPricingApplyHere =
      isCustomerLoggedIn &&
      isMemberDiscountPriceValidOnItsOwn &&
      memberDiscountedPrice <= saleDiscountedPrice;
    const doesSalesPricingApplyHere = saleDiscountedPrice > 0;

    if (doesMemberPricingApplyHere) {
      itemPrice = memberDiscountedPrice;
      itemPriceNormally = memberItemPrice_priceNormally;
      isMemberPrice = true;
    } else if (doesSalesPricingApplyHere) {
      itemPrice = saleDiscountedPrice;
      itemPriceNormally = saleItemPrice_priceNormally;
      isSalePrice = true;
    } else {
      itemPrice = defaultItemPrice;
    }
  }
  itemPriceNormally = itemPriceNormally || itemPrice;

  const subtotal = itemPrice * itemQuantityToDisplay;
  const subtotalNormally = itemPriceNormally * itemQuantityToDisplay;

  let imageThumbnail;
  if (thisProduct && thisProduct.thumbnail?.url) {
    imageThumbnail = thisProduct.thumbnail.url;
  }

  let asAGiftcard: MyBagPresentationItem_AsAGiftcard | undefined = undefined;
  const isThisAGiftcardItem = figureOut_ifThisItemIsAGiftcard(item);
  if (isThisAGiftcardItem) {
    let sendTo;
    const giftcard_attributes = item.giftcard_attributes;
    if (giftcard_attributes) {
      sendTo = item.giftcard_attributes?.giftcard_recipient_email;
    }

    if (!!sendTo) {
      asAGiftcard = {
        sendTo,
      };
    } else {
      return null;
    }
  }

  const presentationMyBagItem: MyBagPresentationItem = {
    id,
    productId: thisProduct?.id === undefined ? "" : String(thisProduct.id),
    // the server-confirmed line, not the quantity a pending or rejected change shows
    lineQuantity: item.quantity,
    lineSubtotal: itemPrice * item.quantity,

    title,
    distinguishingFeature,
    sku,
    howManyOfThisProduct: itemQuantityToDisplay,
    url,

    itemPrice,
    itemPriceNormally,

    isMemberPrice,
    isSalePrice,

    subtotal,
    subtotalNormally,

    imageThumbnail,

    currentStockStatus,
    serverErrors,
    isUnavailable,

    asAGiftcard,
  };

  if (isThisAGiftcardItem && itemQuantityToDisplay > 1) {
    presentationMyBagItem.howManyOfThisProduct = 1;
    const eachGiftcardIsADistinctPresentationItem = Array.apply(
      null,
      Array(itemQuantityToDisplay)
    ).map(() => presentationMyBagItem);
    return eachGiftcardIsADistinctPresentationItem;
  }

  return [presentationMyBagItem];
};

type Quantity = number;
const determineStockStatusAndQuantityForAnItem_givenWorkingModifications = (
  item: CartItemInterface,
  workingModificationsToQuantity: MyBagItemsRunningState_WorkingModificationsToQuantity[],
  cartId?: string
): [MyBagPresentationItem_StockStatus, Quantity] => {
  let determinedStockStatus =
    getCartItemStockStatus(item);
  let itemQuantityToDisplay = item.quantity;
  if (cartId) {
    const workingModification = workingModificationsToQuantity.find(
      (lhs) => lhs.cartId === cartId && lhs.cartItemToUpdateId === item.id
    );

    if (
      workingModification?.status ===
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.pending
    ) {
      itemQuantityToDisplay = workingModification.quantity;
    }

    if (
      workingModification?.status ===
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.approved
    ) {
      itemQuantityToDisplay = workingModification.quantity;
    }

    if (
      workingModification?.status ===
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.rejectedOutOfStock
    ) {
      itemQuantityToDisplay = workingModification.quantity;
      determinedStockStatus = MyBagPresentationItem_StockStatus.OUT_OF_STOCK;
    }
  }

  return [determinedStockStatus, itemQuantityToDisplay];
};

const applyWorkingModificationsToQuantity = (
  presentationMyBagItems: MyBagPresentationItem[],
  workingModificationsToQuantity: MyBagItemsRunningState_WorkingModificationsToQuantity[],
  cartId?: string
) => {
  if (!cartId) {
    return presentationMyBagItems;
  }
  return presentationMyBagItems.map((presentationMyBagItem) => {
    const workingModification = workingModificationsToQuantity.find(
      (lhs) =>
        lhs.cartId === cartId &&
        lhs.cartItemToUpdateId === presentationMyBagItem.id
    );
    if (!workingModification) {
      return presentationMyBagItem;
    }

    const workingModificationStatesThatAreAllowedToShow: `${
      | string
      | MyBagItemsRunningState_WorkingModificationsToQuantity_Status}`[] = [
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.approved,
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.pending,
    ];
    const workingModificationStatesThatAreOutOfStock: `${
      | string
      | MyBagItemsRunningState_WorkingModificationsToQuantity_Status}`[] = [
      MyBagItemsRunningState_WorkingModificationsToQuantity_Status.rejectedOutOfStock,
    ];

    if (
      workingModificationStatesThatAreAllowedToShow.includes(
        workingModification.status
      )
    ) {
      return {
        ...presentationMyBagItem,
        howManyOfThisProduct: workingModification.quantity,
      };
    }

    if (
      workingModificationStatesThatAreOutOfStock.includes(
        workingModification.status
      )
    ) {
      return {
        ...presentationMyBagItem,
        currentStockStatus: MyBagPresentationItem_StockStatus.OUT_OF_STOCK,
      };
    }

    return presentationMyBagItem;
  });
};

const calculateOrderTotal_withoutAnyDiscountsOrAnything = (
  isCustomerLoggedIn: boolean,
  myBagItems_fromTheResponse: CartItemInterface[],
  myBagItems: MyBagPresentationModelConsumerInterface["presentationMyBagItems"],
  cartId?: string
) => {
  const orderTotal = myBagItems.reduce((acc, thisItem) => {
    const thisItem_fromTheResponse = myBagItems_fromTheResponse.find(
      (lhs) => lhs.id === thisItem.id
    );

    let howMuchToAddToTheTotal = 0;
    if (thisItem_fromTheResponse) {
      const prices_forThisItem_fromTheResponse =
        thisItem_fromTheResponse.prices;
      if (prices_forThisItem_fromTheResponse) {
        let howMuchOffForThisItem = 0;
        const thisProduct_fromTheResponse = thisItem_fromTheResponse.product;
        const priceRange_forThisItem_fromTheResponse =
          thisProduct_fromTheResponse.price_range;
        const thisDiscount =
          priceRange_forThisItem_fromTheResponse.minimum_price.discount;
        if (thisDiscount && thisDiscount.amount_off) {
          howMuchOffForThisItem =
            thisDiscount.amount_off * thisItem.howManyOfThisProduct;
        }

        howMuchToAddToTheTotal =
          prices_forThisItem_fromTheResponse.row_total_including_tax.value +
          howMuchOffForThisItem;
      }
    }

    return acc + howMuchToAddToTheTotal;
  }, 0);
  return orderTotal;
};

const calculate_memberPriceForThisItem = (
  myBagItems_fromTheResponse: CartItemInterface[],
  thisItem: MyBagPresentationItem,
  price_tiers_to_select_key: keyof ProductInterface = "price_tiers"
) => {
  const thisItem_fromTheResponse = myBagItems_fromTheResponse.find(
    (lhs) => lhs.id === thisItem.id
  );

  let this_memberDiscountsAppliedOnTheOrderTotal = undefined;
  if (thisItem_fromTheResponse) {
    const thisProduct_fromTheResponse = thisItem_fromTheResponse.product;

    const thisPriceTierPropertyUnchecked = (
      thisProduct_fromTheResponse as { [key: string]: any }
    )[price_tiers_to_select_key] as TierPrice[];

    const thisPriceTier =
      thisPriceTierPropertyUnchecked &&
      thisPriceTierPropertyUnchecked.length > 0
        ? thisPriceTierPropertyUnchecked[0]
        : null;
    if (thisPriceTier) {
      const thisPriceTierDiscount = thisPriceTier.discount;
      if (thisPriceTierDiscount && thisPriceTierDiscount.amount_off) {
        this_memberDiscountsAppliedOnTheOrderTotal =
          thisPriceTierDiscount.amount_off * thisItem.howManyOfThisProduct;
      }
    }
  }

  return this_memberDiscountsAppliedOnTheOrderTotal;
};
const calculate_saleDiscountsAppliedOnTheOrderTotal = (
  isCustomerLoggedIn: boolean,
  myBagItems_fromTheResponse: CartItemInterface[],
  myBagItems: MyBagPresentationModelConsumerInterface["presentationMyBagItems"],
  cartId?: string
) => {
  const saleDiscountsAppliedOnTheOrderTotal = myBagItems.reduce(
    (acc, thisItem) => {
      const this_memberDiscountsAppliedOnTheOrderTotal = isCustomerLoggedIn
        ? calculate_memberPriceForThisItem(myBagItems_fromTheResponse, thisItem)
        : null;
      if (
        this_memberDiscountsAppliedOnTheOrderTotal !== null &&
        this_memberDiscountsAppliedOnTheOrderTotal !== undefined
      ) {
        return acc;
      }

      const thisItem_fromTheResponse = myBagItems_fromTheResponse.find(
        (lhs) => lhs.id === thisItem.id
      );

      let this_saleDiscountsAppliedOnTheOrderTotal = 0;
      if (thisItem_fromTheResponse) {
        const prices_forThisItem_fromTheResponse =
          thisItem_fromTheResponse.prices;
        if (prices_forThisItem_fromTheResponse) {
          const thisProduct_fromTheResponse = thisItem_fromTheResponse.product;
          const priceRange_forThisItem_fromTheResponse =
            thisProduct_fromTheResponse.price_range;
          const thisDiscount =
            priceRange_forThisItem_fromTheResponse.minimum_price.discount;
          if (thisDiscount && thisDiscount.amount_off) {
            this_saleDiscountsAppliedOnTheOrderTotal =
              thisDiscount.amount_off * thisItem.howManyOfThisProduct;
          }
        }
      }

      return acc + this_saleDiscountsAppliedOnTheOrderTotal;
    },
    0
  );
  return saleDiscountsAppliedOnTheOrderTotal;
};
const calculate_memberDiscountsAppliedOnTheOrderTotal = (
  isCustomerLoggedIn: boolean,
  myBagItems_fromTheResponse: CartItemInterface[],
  myBagItems: MyBagPresentationModelConsumerInterface["presentationMyBagItems"]
) => {
  const potentialMemberDiscounts = myBagItems.reduce((acc, thisItem) => {
    const this_memberDiscountsAppliedOnTheOrderTotal =
      calculate_memberPriceForThisItem(
        myBagItems_fromTheResponse,
        thisItem,
        "potential_price_tiers"
      ) || 0;

    return acc + this_memberDiscountsAppliedOnTheOrderTotal;
  }, 0);
  const memberDiscountsAppliedOnTheOrderTotal = isCustomerLoggedIn
    ? myBagItems.reduce((acc, thisItem) => {
        const this_memberDiscountsAppliedOnTheOrderTotal =
          calculate_memberPriceForThisItem(
            myBagItems_fromTheResponse,
            thisItem
          ) || 0;

        return acc + this_memberDiscountsAppliedOnTheOrderTotal;
      }, 0)
    : 0;
  return [potentialMemberDiscounts, memberDiscountsAppliedOnTheOrderTotal];
};

const figureOut_allThePromotionalDiscounts = (
  myBagFromTheResponse?: CheckoutModel_MyBag
) => {
  const allThePromotionalDiscounts_withPrices = (
    ((myBagFromTheResponse || {}).prices || {}).discounts || []
  ).map((discountWithPrice) => {
    const promotionalDiscount: MyBagPresentationOrder_PromotionalDiscount = {
      presentableLabel: discountWithPrice.label,
      value: discountWithPrice.amount.value,
    };
    return promotionalDiscount;
  });

  type ValidValue<T> = Exclude<T, null | undefined | 0 | "" | false>;
  const BooleanFilter = <T extends any>(x: T): x is ValidValue<T> => Boolean(x);
  const allThePromotionalDiscounts_withoutPrices = (
    myBagFromTheResponse?.applied_coupons || []
  )
    .map((discountWithoutPrice) => {
      const isThisDiscountOneWithAPrice =
        allThePromotionalDiscounts_withPrices.find(
          (lhs) => lhs.presentableLabel === discountWithoutPrice.label
        );
      const thisIsDefinitelyADiscountWithoutAPrice =
        !isThisDiscountOneWithAPrice;
      if (thisIsDefinitelyADiscountWithoutAPrice) {
        const promotionalDiscount: MyBagPresentationOrder_PromotionalDiscount =
          {
            presentableLabel: discountWithoutPrice.label,
          };
        return promotionalDiscount;
      }
      return null;
    })
    .filter(BooleanFilter);
  return [...allThePromotionalDiscounts_withPrices];
};

export const figureOut_shippingValueBreakdown = (
  myBagTotalsData?: MyBagTotals_Response
): MyBagPresentationOrder["shippingValueBreakdown"] => {
  // The REST totals-information response is authoritative: the totals data
  // source posts the backend-decided shipping method (see
  // figureOut_shippingMethodCorrection), so base_shipping_incl_tax is 0
  // exactly when the cart qualifies for free shipping. No re-deriving via a
  // frontend threshold (the old 150 hardcode disagreed with the store config)
  // and no reading available_shipping_methods[0] (carrier sort order is not
  // the selected method).
  const shippingTotal = myBagTotalsData?.base_shipping_incl_tax;
  if (shippingTotal === undefined) {
    return undefined;
  }

  const shippingValueBreakdown: MyBagPresentationOrder["shippingValueBreakdown"] =
    {
      shippingTotal,
      isFreeShipping: shippingTotal === 0,
    };

  return shippingValueBreakdown;
};

const figureOut_giftCardValueBreakdown = (
  myBagData?: CheckoutModel_MyBag
): MyBagPresentationOrder["giftCardValueBreakdown"] => {
  const maskGiftCard = (code: string) => {
    const codes = code.split("-");
    return `${codes[0]}************${codes.pop()}`;
  };

  if (myBagData) {
    const appliedGiftCards = myBagData.applied_gift_cards;
    const giftCardValueBreakdowns: Required<MyBagPresentationOrder>["giftCardValueBreakdown"] = [];

    if (appliedGiftCards && appliedGiftCards.length > 0) {
      for (const giftCard of appliedGiftCards) {
        if (giftCard && giftCard.code) {
          const privateBackendCode: Required<MyBagPresentationOrder>["giftCardValueBreakdown"]["privateBackendCode"] =
            giftCard.code;

          const presentableCode: Required<MyBagPresentationOrder>["giftCardValueBreakdown"]["presentableCode"] =
            maskGiftCard(giftCard.code);

          const currentBalance = giftCard.current_balance
            ? giftCard.current_balance.value || 0
            : 0;

          const appliedValue: Required<MyBagPresentationOrder>["giftCardValueBreakdown"]["appliedValue"] =
            giftCard.applied_balance
              ? giftCard.applied_balance.value || 0
              : 0;

          const postApplicationValueLeftOnTheGiftcard: Required<MyBagPresentationOrder>["giftCardValueBreakdown"]["postApplicationValueLeftOnTheGiftcard"] =
            currentBalance - appliedValue;

          const breakdown = {
            privateBackendCode,
            presentableCode,
            appliedValue,
            postApplicationValueLeftOnTheGiftcard,
          };

          giftCardValueBreakdowns.push(breakdown);
        }
      }

      return giftCardValueBreakdowns
    }
  }

  return undefined;
};

export const figureOut_totalValueBreakdown = (
  // Retained for arity/signature stability (the sole caller and the co-located
  // tests pass 3 args); unused in the body since the per-country gstQuotient
  // discount correction was removed (M2-1925 #5 discount-gst).
  localization: LocalizationConsumerInterface,
  myBagData?: CheckoutModel_MyBag,
  myBagTotalsData?: MyBagTotals_Response
): MyBagPresentationOrder["totalValueBreakdown"] => {
  const myBagPrices = myBagData?.prices;

  const myBagShippingAddresses = myBagData?.shipping_addresses;
  const areThereShippingAddresses = (myBagShippingAddresses?.length || 0) > 0;
  const areThereNotAnyShippingAddresses =
    myBagShippingAddresses && myBagShippingAddresses.length < 1;

  const isThereEverythingExceptTaxInformation =
    areThereShippingAddresses &&
    myBagPrices &&
    myBagPrices.grand_total &&
    myBagPrices.grand_total.value &&
    myBagPrices.applied_taxes &&
    myBagPrices.applied_taxes.length === 0;

  let value = 0;
  let gst = 0;
  if (
    areThereShippingAddresses &&
    myBagPrices &&
    myBagPrices.applied_taxes &&
    myBagPrices.applied_taxes.length > 0 &&
    myBagPrices.grand_total &&
    myBagPrices.grand_total.value
  ) {
    // The quote's totals are authoritative now that the totals data source
    // posts the backend-decided shipping method (no phantom flatrate) — no
    // display-side shipping subtraction here or in the sibling branch below.
    value = myBagPrices.grand_total.value;
    gst = myBagPrices.applied_taxes[0].amount.value;
  } else if (
    (areThereNotAnyShippingAddresses ||
      isThereEverythingExceptTaxInformation) &&
    myBagTotalsData
  ) {
    // base_tax_amount from the REST totals-information payload is ALREADY the
    // discounted, tax-inclusive GST (Magento apply_after_discount=1 default +
    // BBNT price_includes_tax=1 / discount_tax=1 — vendor/magento/module-tax/
    // etc/config.xml:18 and app/code/BBNT/Base/Setup/UpgradeData.php:468-470).
    // Trust it directly, exactly as Branch A trusts applied_taxes[0].amount.value.
    // The former gstQuotient/discountGst correction subtracted the discount's GST
    // a SECOND time, showing "GST Included" too low on AU/NZ discounted carts.
    value = myBagTotalsData.base_grand_total;
    gst = myBagTotalsData.base_tax_amount || 0;
  }

  const totalValueBreakdown: MyBagPresentationOrder["totalValueBreakdown"] = {
    value,
    gst,
  };

  return totalValueBreakdown;
};

const figureOut_ifThisItemIsAGiftcard = (myBagItem: CartItemInterface) => {
  const isThisAGiftcardItem = myBagItem.product.url_key === "gift-cards";
  return isThisAGiftcardItem;
};

const figureOut_areThereAnyGiftcardsInTheOrder = (
  myBagData?: CheckoutModel_MyBag
) => {
  const areThereAnyGiftcardsInTheOrder = (myBagData?.items || []).some(
    (myBagItem) => figureOut_ifThisItemIsAGiftcard(myBagItem)
  );
  return areThereAnyGiftcardsInTheOrder;
};

const figureOut_isThisAVirtualOrder = (
  areThereAnyGiftcardsInTheOrder: boolean,
  myBagData?: CheckoutModel_MyBag
) => {
  if (areThereAnyGiftcardsInTheOrder) {
    const isThereAnyThingOtherThanGiftcardsInTheOrder = (
      myBagData?.items || []
    ).some((myBagItem) => myBagItem.product.url_key !== "gift-cards");
    if (!isThereAnyThingOtherThanGiftcardsInTheOrder) {
      return true;
    }
  }
  return false;
};

const figureOut_promoCodeBreakdown = (
  myBagData?: CheckoutModel_MyBag
): MyBagPresentationOrder["promoCodeBreakdown"] => {
  let code;
  if (
    myBagData &&
    myBagData.applied_coupons &&
    myBagData.applied_coupons.length > 0
  ) {
    code = myBagData.applied_coupons[0].code;
  }

  if (!code) {
    return undefined;
  }
  const promoCodeBreakdown: MyBagPresentationOrder["promoCodeBreakdown"] = {
    code,
  };

  return promoCodeBreakdown;
};

const generateMyBagPresentationShippingDetailsOnCart = (
  myBagData?: CheckoutModel_MyBag
): MyBagPresentationShippingDetailsOnCart | undefined => {
  if (!myBagData?.email) {
    return undefined;
  }

  const thisShippingAddress =
    myBagData &&
    myBagData.shipping_addresses &&
    myBagData.shipping_addresses.length > 0
      ? myBagData.shipping_addresses[0]
      : undefined;
  if (!thisShippingAddress || thisShippingAddress.street?.length < 1) {
    return undefined;
  }

  const value: MyBagPresentationShippingDetailsOnCart = {
    firstName: thisShippingAddress.firstname,
    lastName: thisShippingAddress.lastname,
    addressLine1: thisShippingAddress.street[0],
    addressLine2:
      thisShippingAddress.street.length > 1
        ? thisShippingAddress.street[1]
        : undefined,
    city: thisShippingAddress.city,
    stateLabel: thisShippingAddress.region.label,
    postcode: thisShippingAddress.postcode,
    country: thisShippingAddress.country.label as Uppercase<string>,
    phone: thisShippingAddress.telephone,
    email: myBagData?.email,
  };
  return value;
};
